import { Box, Divider, Flex, Grid } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import i18next from "i18next";

import GameSearchFilter from "./GameSearchFilter";
import GameItem from "../../GameItem/GameItem";
import NotFound from "../../ProgressBlock/NotFound";
import { languageTransfer } from "../../../utils/languageTransfer";

/**
 * GameSearchContent — 搜尋彈窗內容(移植自 911vip 並適配 zun789)
 * 資料取自 redux state.isHandleAllGame;zun789 隨機清單欄位為 randomlySelectedGames。
 */
const GameSearchContent = ({ onClose = () => {} } = {}) => {
  const gamesInfo = useSelector((state) => state.isHandleAllGame);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState(null);
  const [highlightUid, setHighlightUid] = useState(null);

  const gameList = useMemo(() => {
    const {
      newGameList = [],
      topGameList = [],
      allGames = [],
      favoriteGameList = [],
      randomlySelectedGames = [],
    } = gamesInfo || {};

    let baseList = topGameList;

    if (filterCategory) {
      let targetList = [];
      switch (filterCategory) {
        case "new":
          targetList = newGameList;
          break;
        case "top":
          targetList = topGameList;
          break;
        case "favorite":
          targetList = favoriteGameList;
          break;
        case "random":
          targetList = randomlySelectedGames;
          break;
        default:
          targetList = [];
      }
      const targetUids = new Set(targetList?.map((g) => g.gameUid));
      baseList = baseList?.filter((g) => targetUids.has(g.gameUid));
    }

    if (searchQuery && searchQuery.trim().length >= 2) {
      const lowerQuery = searchQuery.toLowerCase();
      return allGames?.filter((g) => {
        const combinedNames = `${g.gameNameEn}${g.gameNameTw}${g.gameNameVi}${g.gameNameZh}`;
        return combinedNames.toLowerCase().includes(lowerQuery);
      });
    }

    return baseList;
  }, [gamesInfo, filterCategory, searchQuery]);

  // 從遊戲返回時,捲回並標記上次點擊的卡片
  useEffect(() => {
    const savedUid = sessionStorage.getItem("lastClickedGameUid");
    if (gameList?.length > 0 && savedUid) {
      setHighlightUid(savedUid);
      const timer = setTimeout(() => {
        const el = document.getElementById(`search-item-${savedUid}`);
        if (el) el.scrollIntoView({ behavior: "auto", block: "center" });
        sessionStorage.removeItem("lastClickedGameUid");
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [gameList]);

  const handleClearFilters = () => {
    setSearchQuery("");
    setFilterCategory(null);
  };

  return (
    <Box>
      {/* 搜尋列 sticky 固定在頂,結果隨 Modal body 上下捲動;
          zIndex 需高於遊戲卡片的 HOT 標籤/愛心(絕對定位),否則捲動時會被蓋住 */}
      <Box position="sticky" top="0" zIndex={30} bg="#24123A" pt={1} pb={2}>
        <GameSearchFilter
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          gamesInfo={gamesInfo}
          filterCategory={filterCategory}
          setFilterCategory={setFilterCategory}
          onClearFilters={handleClearFilters}
        />
        <Divider borderColor="rgba(199,155,255,0.2)" mt={1} />
      </Box>

      {gameList?.length === 0 ? (
        <Box py={8}>
          <NotFound />
        </Box>
      ) : (
        <Box w="100%" pt={3}>
          <Grid
            templateColumns={{
              base: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            }}
            gap={2}
            py={4}
          >
            {gameList?.map((game, i) => {
              const gameName =
                game?.[
                  `gameName${languageTransfer({ lang: i18next.language })}` ||
                    "gameNameEn"
                ];
              const isHighlighted = highlightUid === game.gameUid;

              return (
                <Box
                  key={game.gameUid || game.uid || i}
                  id={`search-item-${game.gameUid}`}
                  position="relative"
                  zIndex={0}
                  mb={{ base: 0, md: 3 }}
                  // zun789 GameItem 不吃 onCloseFunction:改由外層記錄並關閉彈窗
                  onClick={() => {
                    sessionStorage.setItem("lastClickedGameUid", game.gameUid);
                    onClose();
                  }}
                  onMouseEnter={() => {
                    if (isHighlighted) setHighlightUid(null);
                  }}
                >
                  <GameItem
                    gameProps={{
                      gameName,
                      platform: game?.game_platform,
                      gameCode: game?.gameCode,
                      gameType: game?.gameType,
                      gameNameEn: game?.gameNameEn,
                      gameSubType: game?.game_subtype,
                    }}
                    originProps={game}
                  />
                  {/* zun789 GameItem 沒有 otherComponents,LAST VIEWED 遮罩改疊在卡片外層 */}
                  {isHighlighted && (
                    <Flex
                      position="absolute"
                      inset="0"
                      bg="rgba(0,0,0,0.6)"
                      borderRadius="15px"
                      zIndex="10"
                      align="center"
                      justify="center"
                      pointerEvents="none"
                      backdropFilter="blur(2px)"
                      border="2px solid"
                      borderColor="#C79BFF"
                      color="white"
                      fontSize="14px"
                      fontWeight="700"
                    >
                      LAST VIEWED
                    </Flex>
                  )}
                </Box>
              );
            })}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default GameSearchContent;
